import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { Snackbar, Alert, AlertColor } from '@mui/material';

interface ToastContextValue {
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
  showInfo: (message: string) => void;
  showWarning: (message: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

type Queued = { message: string; severity: AlertColor };

export function ToastProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState<Queued>({ message: '', severity: 'success' });
  const queueRef = React.useRef<Queued[]>([]);

  const show = useCallback((msg: string, sev: AlertColor) => {
    queueRef.current.push({ message: msg, severity: sev });
    setOpen((isOpen) => {
      if (!isOpen && queueRef.current.length > 0) {
        const n = queueRef.current.shift()!;
        setCurrent(n);
        return true;
      }
      return isOpen;
    });
  }, []);

  const showSuccess = useCallback((msg: string) => show(msg, 'success'), [show]);
  const showError = useCallback((msg: string) => show(msg, 'error'), [show]);
  const showInfo = useCallback((msg: string) => show(msg, 'info'), [show]);
  const showWarning = useCallback((msg: string) => show(msg, 'warning'), [show]);

  const handleClose = useCallback(() => {
    setOpen(false);
    setTimeout(() => {
      if (queueRef.current.length > 0) {
        const n = queueRef.current.shift()!;
        setCurrent(n);
        setOpen(true);
      }
    }, 150);
  }, []);

  return (
    <ToastContext.Provider value={{ showSuccess, showError, showInfo, showWarning }}>
      {children}
      <Snackbar
        open={open}
        autoHideDuration={5000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleClose} severity={current.severity} variant="filled" sx={{ width: '100%' }}>
          {current.message}
        </Alert>
      </Snackbar>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}
